// apps/merchant-v3/src/components/banners/OverdueInvoiceBanner.tsx
//
// Phase 6 Wave 12 Task 42 — Yellow banner shown when the merchant has at
// least one overdue invoice. Reads
// `useBillingSummary().overdue_count` and renders only when both:
//   1. overdue_count > 0
//   2. the merchant has not dismissed the banner in this session
//      (sessionStorage flag — re-appears on next sign-in / reload).
//
// Pluralised copy (1 overdue / N overdue) via i18next `count` interpolation.
// CTA jumps to /billing/invoices?status=overdue so the merchant lands on
// the filtered list. data-testids: `overdue-banner` (root),
// `overdue-banner-pay-link`, `overdue-banner-dismiss`.
//
// vg-binding: PLAN:task-42
// vg-binding: P6.D-246
// vg-binding: P6.D-247
// vg-binding: P6.D-248
// vg-binding: G-70
// vg-binding: G-71

import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';

import { useTranslation } from '@/lib/i18n';
import { useBillingSummary } from '@/hooks/useBillingSummary';

const SESSION_KEY = 'merchant-v3.banners.overdue.dismissed.v1';

function loadDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function saveDismissed(v: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (v) window.sessionStorage.setItem(SESSION_KEY, '1');
    else window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore quota / private-mode errors
  }
}

export function OverdueInvoiceBanner(): ReactElement | null {
  const { t } = useTranslation('common');
  const summary = useBillingSummary();
  const [dismissed, setDismissed] = useState<boolean>(() => loadDismissed());

  // Mirror state → sessionStorage so a navigation does not unmount the
  // dismissal. Cleared automatically on browser tab close.
  useEffect(() => {
    saveDismissed(dismissed);
  }, [dismissed]);

  // When `overdue_count` flips back to 0 (merchant paid an overdue
  // invoice), reset the dismissal so the banner re-appears the moment a
  // NEW invoice goes overdue.
  useEffect(() => {
    if (summary && summary.overdue_count === 0 && dismissed) {
      setDismissed(false);
    }
  }, [summary, dismissed]);

  if (!summary || summary.overdue_count <= 0 || dismissed) return null;

  const count = summary.overdue_count;

  return (
    <div
      role="alert"
      data-testid="overdue-banner"
      className="flex items-center gap-3 border-b border-yellow-300 bg-yellow-100 px-4 py-2 text-sm text-yellow-900"
    >
      <AlertCircle
        className="h-5 w-5 shrink-0 text-yellow-700"
        aria-hidden
      />
      <p className="flex-1 font-medium">
        {t('banners.overdue.title', {
          count,
          defaultValue:
            count === 1 ? '1 invoice overdue' : '{{count}} invoices overdue',
        })}
      </p>
      <Link
        to="/billing/invoices?status=overdue"
        data-testid="overdue-banner-pay-link"
        className="inline-flex h-8 items-center rounded-md border border-yellow-400 bg-yellow-200 px-3 text-xs font-semibold text-yellow-900 hover:bg-yellow-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500"
      >
        {t('banners.overdue.cta', { defaultValue: 'Pay now' })}
      </Link>
      <button
        type="button"
        data-testid="overdue-banner-dismiss"
        aria-label={t('banners.overdue.dismiss', { defaultValue: 'Dismiss' })}
        onClick={() => setDismissed(true)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-yellow-800 hover:bg-yellow-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500"
      >
        <span aria-hidden className="text-base">
          ×
        </span>
      </button>
    </div>
  );
}

export default OverdueInvoiceBanner;
